import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import RenderInBody from './RenderInBody';
import './CorgeeModalStats.css';

// Types
interface CorgeeModalStatsProps {
  openModal?: boolean;
  onClose?: () => void;
  onSave?: () => void;
  className?: string;
  title?: string;
  actionButtons?: React.ReactNode[];
  isDraggable?: boolean;
  closeOnEscape?: boolean;
  textButton?: string;
  height?: number;
  width?: number;
  bodyClassName?: string;
  saveButtonColor?: string;
  allowSaveAndNewCheckBox?: boolean;
  hideActionButtons?: boolean;
  hasMaxHeight?: boolean;
  allowMinimize?: boolean;
  allowMaximize?: boolean;
  children?: React.ReactNode;
}

export interface CorgeeModalStatsRef {
  openModal: (callback?: () => void) => void;
  closeModal: () => void;
}

const CorgeeModalStats = forwardRef<CorgeeModalStatsRef, CorgeeModalStatsProps>(
  (
    {
      openModal: openModalProp = false,
      onClose,
      onSave: _onSave,
      className = '',
      title,
      isDraggable: _isDraggable = false,
      closeOnEscape: _closeOnEscape = false,
      width: widthProp = 500,
      hasMaxHeight = false,
      children,
    },
    ref,
  ) =>
  {
    // State
    const [isOpen, setIsOpen] = useState(openModalProp);

    // Open modal method
    const openModal = useCallback((callback?: () => void) =>
    {
      setIsOpen(true);
      if (callback)
      {
        // Execute callback after state update
        setTimeout(callback, 0);
      }
    }, []);

    // Close modal method
    const closeModal = useCallback(() =>
    {
      setIsOpen(false);
    }, []);

    // Expose methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        openModal,
        closeModal,
      }),
      [openModal, closeModal],
    );

    // Handle close event
    const handleClose = useCallback(() =>
    {
      if (onClose && typeof onClose === 'function')
      {
        onClose();
      }
    }, [onClose]);

    // Sync with prop changes
    useEffect(() =>
    {
      if (isOpen !== openModalProp)
      {
        setIsOpen(openModalProp);
      }
    }, [openModalProp]);

    // Computed width
    const width = widthProp && widthProp !== 0 ? widthProp : 500;

    return (
      <div>
        {isOpen && (
          <RenderInBody>
            <div className="corgee_com_modal-backdrop" onClick={handleClose}>
              <div
                className={`corgee_com_conversation-metadata-modal-stats _DEL_corgee-modal-container ${className}`}
                style={{ width: `${width}px` }}
                onClick={e => e.stopPropagation()}
              >
                <div className="corgee_com_global-modal_header">
                  <div className="header-info">
                    <div className="react-add-new-card-title" id="contained-modal-title-sm">
                      {title}
                    </div>
                    <button className="close-control" onClick={handleClose}>
                      <i className="icon-incorrect-icon"></i>
                    </button>
                  </div>
                </div>

                <div
                  className={
                    hasMaxHeight
                      ? 'corgee_com_global-modal_body modal_body-max-height'
                      : 'corgee_com_global-modal_body'
                  }
                >
                  {children}
                </div>
              </div>
            </div>
          </RenderInBody>
        )}
      </div>
    );
  },
);

CorgeeModalStats.displayName = 'CorgeeModalStats';

export default CorgeeModalStats;
